// frontend/src/utils/checkoutBridge.ts
//
// FastStar 结算页宿主桥接工具。
// 详见 sdk/INTEGRATION.md。结算页在支付「成功 / 取消 / 失败」状态落定时，
// 调用 notifyCheckoutResult() 一次即可，按以下优先级把结果回传给宿主环境：
//   1. Flutter WebView（window.FastStarBridge 存在）
//   2. iframe 弹层（URL 带 embedded=1 或 window.self !== window.top）
//   3. 普通浏览器跳转（重定向到 success_url / cancel_url 并附加 faststar_status）

export type CheckoutEventName =
  | 'checkout.success'
  | 'checkout.cancel'
  | 'checkout.error';

export type CheckoutStatus = 'success' | 'cancel' | 'error';

export interface CheckoutResultData {
  payment_intent_id?: string;
  message?: string;
  code?: string;
  reason?: string;
  [key: string]: unknown;
}

// 让 TypeScript 认得 Flutter 注入的桥接频道
declare global {
  interface Window {
    FastStarBridge?: {
      postMessage: (message: string) => void;
    };
  }
}

/**
 * 判断当前结算页是否运行在 iframe 弹层（JS SDK）中。
 * 判定：URL 带 embedded=1，或 window.self !== window.top。
 */
export function isEmbedded(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('embedded') === '1') {
      return true;
    }
    return window.self !== window.top;
  } catch {
    // 跨域访问 window.top 会抛错，说明确实在 iframe 内
    return true;
  }
}

/**
 * 判断是否运行在 Flutter WebView（注入了 FastStarBridge）中。
 */
export function hasFlutterBridge(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.FastStarBridge &&
    typeof window.FastStarBridge.postMessage === 'function'
  );
}

/**
 * 推断 postMessage 的目标 origin（宿主页 origin）。
 * 收银台被 iframe 嵌入时，宿主页 origin 由 document.referrer 给出。
 * 取不到（同源跳转把 referrer 清空、或 referrer 被策略屏蔽）时返回 null，
 * 调用方应放弃 postMessage 而非退化为 '*'（避免把支付结果广播给任意父窗口）。
 */
function hostTargetOrigin(): string | null {
  try {
    if (document.referrer) {
      return new URL(document.referrer).origin;
    }
  } catch {
    // referrer 非法时忽略
  }
  // 退化：用祖先 origin（部分浏览器在 sandbox 下可用），否则放弃
  try {
    const anc = (window as unknown as { location: { ancestorOrigins?: DOMStringList } })
      .location.ancestorOrigins;
    if (anc && anc.length > 0) {
      return anc[0];
    }
  } catch {
    // 忽略
  }
  return null;
}

/**
 * 在 redirectUrl 上附加查询参数。
 */
function appendQuery(
  redirectUrl: string,
  params: Record<string, string>
): string {
  try {
    const url = new URL(redirectUrl, window.location.origin);
    Object.keys(params).forEach((k) => {
      if (params[k] !== undefined && params[k] !== null) {
        url.searchParams.set(k, String(params[k]));
      }
    });
    return url.toString();
  } catch {
    // redirectUrl 非法时退化为简单拼接
    const sep = redirectUrl.includes('?') ? '&' : '?';
    const qs = Object.keys(params)
      .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
      .join('&');
    return `${redirectUrl}${sep}${qs}`;
  }
}

/**
 * 向宿主环境回传结算结果。
 *
 * @param status      'success' | 'cancel' | 'error'
 * @param data        透传数据，例如 { payment_intent_id } 或 { message }
 * @param redirectUrl 普通模式下的 success_url / cancel_url（可选）
 * @returns 是否已被宿主接管（true 时结算页无需再渲染自己的结果界面）
 */
export function notifyCheckoutResult(
  status: CheckoutStatus,
  data: CheckoutResultData = {},
  redirectUrl?: string
): boolean {
  const event: CheckoutEventName = `checkout.${status}`;

  // 1. Flutter WebView 桥接（只接受字符串，需要 JSON.stringify，不带 source 字段）
  if (hasFlutterBridge()) {
    try {
      window.FastStarBridge!.postMessage(
        JSON.stringify({ event, data: data || {} })
      );
      return true;
    } catch {
      // 桥接失败时继续尝试下面的方式
    }
  }

  // 2. iframe 弹层（JS SDK）—— 发给 window.parent，带 source: 'faststar'。
  //    target origin 用宿主页 origin（document.referrer / ancestorOrigins），
  //    绝不用 '*'：否则支付结果（payment_intent_id 等）会被广播给任意父窗口。
  if (isEmbedded() && window.parent && window.parent !== window) {
    const targetOrigin = hostTargetOrigin();
    if (targetOrigin) {
      try {
        window.parent.postMessage(
          { source: 'faststar', event, data: data || {} },
          targetOrigin
        );
        return true;
      } catch {
        // 继续尝试重定向
      }
    }
    // 取不到宿主 origin：仍视为已嵌入（避免在 iframe 里又跳 redirectUrl，
    // 造成宿主页内整页跳转）。SDK 会在用户关闭弹层时收到 cancel。
    return true;
  }

  // 3. 普通跳转模式（重定向 + faststar_status）
  if (redirectUrl) {
    const target = appendQuery(redirectUrl, {
      faststar_status: status,
      ...Object.keys(data).reduce<Record<string, string>>((acc, k) => {
        const v = data[k];
        if (v !== undefined && v !== null) {
          acc[k] = String(v);
        }
        return acc;
      }, {}),
    });
    window.location.href = target;
    return true;
  }

  // 无宿主、无 redirect URL：由结算页自行展示结果页
  return false;
}
